import { Form } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import { admin_cse } from "@/config/roles";
import { Button, ButtonSizes, ButtonTypes, CustomForm } from "@/component-library";
import { Button2 } from "@/ui2/atoms/Button";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import EditIcon from "@/assets/images/icons2/icon--edit.svg?component";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { isAuthorized } from "@/utils/authToken";
import { localTimestampFromStandardTimestamp } from "@/utils/momentUtils";
import { Modal2 } from "@/ui2/molecules/Modal";
import { ProviderEnum } from "@/core/constants/provider";
import { showToast } from "@/providers/ToastProvider";
import { toastMessageTypes } from "@/redux/actions/toaster";
import { XSS_REGEX } from "@/constants/security";

import "./PatientArticulatedGoals.styles.scss";

const DESCRIPTION_TEXT = "How would the patient like to change their life?";

export const PatientArticulatedGoals = (props) => {
  const profile = useSelector((state) => state.auth.profile);
  const { existingGoals, refreshGoals } = props;
  const dispatch = useDispatch();

  const onModalSubmission = async (newArticulatedGoals, close) => {
    dispatch(showLoader());
    try {
      await carePlanRequests.update_patient_articulated_goals({
        context: {
          exceptionConfig: {
            CarePlanInvalidVersionException: {
              toastDismissTimeout: 300000, // 5 minutes in millis
              toastErrorType: toastMessageTypes.error_v2,
            },
          },
          showLoader: false,
        },
        data: {
          current_version: existingGoals.current_version,
          patient_articulated_goals: newArticulatedGoals,
        },
        urlParams: { patientId: props.patientId },
      });

      await refreshGoals();
      close();

      showToast([{ heading: "Patient articulated goals updated.", variant: "success" }]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const renderExistingGoals = () => {
    const { patient_articulated_goals, updated_by_name, updated_timestamp } = existingGoals;

    return (
      <div className="tw:contents">
        <p
          className={
            "PatientGoalsStrengthsCautions--readonly-text " +
            "PatientArticulatedGoals--readonly-text"
          }
          {...getAutomationDataAttr("PatientArticulatedGoals_existingGoals")}
        >
          {patient_articulated_goals}
        </p>

        <small className="tw:flex tw:justify-between tw:text-md tw:text-text-tertiary">
          <span>Last Updated by {updated_by_name}</span>

          <span>{localTimestampFromStandardTimestamp(updated_timestamp, "M/D/YYYY")}</span>
        </small>
      </div>
    );
  };

  const renderNoGoalsSpecified = () => {
    const isAuthorizedToEditGoals = isAuthorizedToEdit();
    const noPermissionText =
      "Authorized members of the patient's aptihealth care team can update patient articulated goals.";
    const hasPermissionText = 'Choose "Add" to add goals.';
    const textBackgroundClass = `PatientGoalsStrengthsCautions--empty${
      isAuthorizedToEditGoals ? "" : " no-permission"
    }`;

    return (
      <div {...getAutomationDataAttr("PatientArticulatedGoals_emptyGoals")}>
        <div className={textBackgroundClass}>
          <p className="PatientGoalsStrengthsCautions--empty-text">
            {isAuthorizedToEditGoals ? hasPermissionText : noPermissionText}
          </p>
        </div>
      </div>
    );
  };

  const isAuthorizedToEdit = () => {
    if (isAuthorized(admin_cse)) {
      return profile?.is_edit_care_plan_enabled;
    } else {
      return (
        props.isProviderOnCareTeam &&
        profile?.provider_type &&
        [
          ProviderEnum.BEHAVIORAL,
          ProviderEnum.CASE_MANAGER,
          ProviderEnum.PEER,
          ProviderEnum.PRESCRIBE,
        ].includes(profile.provider_type)
      );
    }
  };

  const editLabel = existingGoals?.patient_articulated_goals ? "Edit" : "Add";

  return (
    <DashboardCard2
      Actions={
        isAuthorizedToEdit() && (
          <Modal2
            overlayProps={{ isDismissable: true }}
            title={`${editLabel} Patient Articulated Goals`}
            trigger={
              <Button2
                icon={<EditIcon />}
                size="sm"
                variant="link"
                {...getAutomationDataAttr("PatientArticulatedGoals_editButton")}
              >
                {editLabel}
              </Button2>
            }
          >
            {({ close }) => (
              <PatientArticulatedGoalsForm
                existingGoals={existingGoals}
                onClose={close}
                onModalSubmission={(goals) => onModalSubmission(goals, close)}
              />
            )}
          </Modal2>
        )
      }
      {...getAutomationDataAttr("PatientArticulatedGoals_container")}
      classNameChildren="tw:gap-3 tw:grid tw:grid-rows-[auto_1fr_auto] tw:h-full tw:max-h-80"
      title="Patient Articulated Goals"
    >
      <small className="tw:text-sm tw:text-text-tertiary">{DESCRIPTION_TEXT}</small>

      {existingGoals?.patient_articulated_goals ? renderExistingGoals() : renderNoGoalsSpecified()}
    </DashboardCard2>
  );
};

const PatientArticulatedGoalsForm = (props) => {
  const { patient_articulated_goals: initialGoals } = props.existingGoals;
  const maxGoalsCharacters = 4000;
  const characterCountExceededError = `Limit text to ${maxGoalsCharacters} characters`;

  const patientArticulatedGoalsSchema = yup.object().shape({
    patientArticulatedGoals: yup
      .string()
      .max(maxGoalsCharacters, characterCountExceededError)
      .test("special characters", "Special characters are not allowed", (value) => {
        return !XSS_REGEX.test(value);
      }),
  });

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="PatientGoalsStrengthsCautions--modal_form">
          <div className="FormField-header_container" data-public>
            <p className="PatientGoalsStrengthsCautions--modal_subtitle">{DESCRIPTION_TEXT}</p>
          </div>

          <div>
            <FormTextArea
              formikProps={formikProps}
              inputClassName="PatientArticulatedGoals--modal-free-text-input"
              name="patientArticulatedGoals"
              testId="PatientArticulatedGoals_textInput"
              triggerErrorOnChange
            />

            {formikProps.errors?.patientArticulatedGoals === characterCountExceededError && (
              <CharacterCounter
                characterLimit={maxGoalsCharacters}
                className="PatientGoalsStrengthsCautions--input_character_counter"
                length={formikProps.values?.patientArticulatedGoals?.length}
              />
            )}
          </div>

          <div className="PatientGoalsStrengthsCautions-button_wrapper">
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryV2}
              type="submit"
            >
              Save
            </Button>

            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryOutlineV2}
              onClick={props.onClose}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <CustomForm
      initialValues={{
        patientArticulatedGoals: initialGoals ?? "",
      }}
      onSubmit={async (formData) => {
        await props.onModalSubmission(formData.patientArticulatedGoals);
      }}
      render={renderForm}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={patientArticulatedGoalsSchema}
    />
  );
};
